/* ==========================================================================
   Kopstorm - Anavrin Kit Finder

   One engine for two sections:
     sections/kopstorm-quiz.liquid         the flow
     sections/kopstorm-quiz-result.liquid  the match screen

   Both can sit on the same page (the result starts hidden and is revealed at
   the end) or on two pages (the result reads the stored answers on load).

   Answers live in sessionStorage under the key printed on the root element,
   so a refresh mid flow does not throw the shopper back to question one.

   Scoring: every option carries data-kq-weights, a "key:points,key:points"
   string. One question is the router and the rest are modifiers. The winning
   key selects a match block on the result; a second answer (the level key)
   selects which of that block's three kits is shown. Nothing about the
   product data is decided here, it is all rendered server side and revealed.
   ========================================================================== */

(function () {
  'use strict';

  var doc = document;
  var win = window;

  /* ------------------------------------------------------------------------
     Small helpers
     ------------------------------------------------------------------------ */

  function all(root, selector) {
    return Array.prototype.slice.call((root || doc).querySelectorAll(selector));
  }

  function one(root, selector) {
    return (root || doc).querySelector(selector);
  }

  function readStore(key) {
    try {
      var raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function writeStore(key, value) {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* private mode, quota, or a browser that refuses. The flow still works
         inside the page, it just does not survive a reload. */
    }
  }

  function clearStore(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (e) {
      /* nothing to do */
    }
  }

  /* Placeholder tokens are written as [name] and never as Liquid output tags,
     because Shopify parses {{ ... }} inside a JSON template setting value as a
     dynamic source and refuses to save the file. */
  function fillTokens(text, answers) {
    if (!text) return '';
    var filled = String(text).replace(/\[([a-z0-9_]+)\]/gi, function (match, name) {
      var value = answers[name];
      if (value === undefined || value === null || value === '') return '';
      return String(value);
    });
    /* A token that resolves to nothing leaves a gap and an orphaned article or
       comma behind it. Tidy those rather than printing "your kit is the ." */
    return filled
      .replace(/\s+([.,!?;:])/g, '$1')
      .replace(/\b(the|de|le)\s+([.,!?])/gi, '$2')
      .replace(/\s{2,}/g, ' ')
      .replace(/^[\s,]+/, '')
      .trim();
  }

  function parseWeights(raw) {
    var out = {};
    if (!raw) return out;
    String(raw)
      .split(',')
      .forEach(function (pair) {
        var bits = pair.split(':');
        if (bits.length !== 2) return;
        var key = bits[0].trim();
        var points = parseFloat(bits[1]);
        if (!key || isNaN(points)) return;
        out[key] = (out[key] || 0) + points;
      });
    return out;
  }

  function openCart() {
    /* Whichever drawer is installed picks this up. The native Kopstorm drawer
       and UpCart both listen, and neither minds hearing it twice. */
    doc.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    doc.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    try {
      if (typeof window.upcartToggleCart === 'function') window.upcartToggleCart();
      else if (window.UpCart && typeof window.UpCart.open === 'function') window.UpCart.open();
    } catch (e) {
      /* the drawer is the app's business, not ours */
    }
  }

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  /* ------------------------------------------------------------------------
     The flow
     ------------------------------------------------------------------------ */

  function Quiz(root) {
    this.root = root;
    this.storeKey = root.getAttribute('data-kq-storage') || 'kopstorm_kit_quiz';
    this.answers = readStore(this.storeKey);
    this.steps = all(root, '[data-kq-step]');
    this.track = one(root, '[data-kq-track]');
    this.count = one(root, '[data-kq-count]');
    this.back = one(root, '[data-kq-back]');
    this.index = 0;
    this.history = [];
    this.loadingTimer = null;
    this.factTimer = null;

    if (!this.steps.length) return;

    this.bind();
    this.paint();
  }

  Quiz.prototype.stepAt = function (index) {
    return this.steps[index] || null;
  };

  /* A step is skipped when it declares a condition that the stored answers do
     not satisfy. Values are compared against a comma list so one screen can
     follow several answers. */
  Quiz.prototype.visible = function (step) {
    if (!step) return false;
    var key = step.getAttribute('data-kq-cond-key');
    if (!key) return true;
    var wanted = (step.getAttribute('data-kq-cond-value') || '')
      .split(',')
      .map(function (value) {
        return value.trim();
      })
      .filter(Boolean);
    if (!wanted.length) return true;
    return wanted.indexOf(String(this.answers[key] || '')) > -1;
  };

  Quiz.prototype.nextIndex = function (from) {
    for (var i = from + 1; i < this.steps.length; i++) {
      if (this.visible(this.steps[i])) return i;
    }
    return -1;
  };

  Quiz.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var option = event.target.closest('[data-kq-option]');
      if (option && self.root.contains(option)) {
        self.choose(option);
        return;
      }

      var next = event.target.closest('[data-kq-next]');
      if (next && self.root.contains(next)) {
        event.preventDefault();
        if (next.getAttribute('aria-disabled') === 'true') return;
        self.advance();
        return;
      }

      var skip = event.target.closest('[data-kq-skip]');
      if (skip && self.root.contains(skip)) {
        event.preventDefault();
        self.advance(true);
        return;
      }

      var back = event.target.closest('[data-kq-back]');
      if (back && self.root.contains(back)) {
        event.preventDefault();
        self.goBack();
      }
    });

    this.root.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var field = event.target.closest('[data-kq-field]');
      if (!field) return;
      event.preventDefault();
      var cta = one(self.stepAt(self.index), '[data-kq-next]');
      if (cta && cta.getAttribute('aria-disabled') !== 'true') self.advance();
    });

    this.root.addEventListener('input', function (event) {
      var field = event.target.closest('[data-kq-field]');
      if (!field) return;
      self.answers[field.getAttribute('data-kq-field')] = field.value.trim();
      writeStore(self.storeKey, self.answers);
      self.syncCta();
    });
  };

  Quiz.prototype.choose = function (option) {
    var step = option.closest('[data-kq-step]');
    if (!step) return;

    var key = step.getAttribute('data-kq-key');
    var value = option.getAttribute('data-kq-option');
    var label = option.getAttribute('data-kq-label') || '';

    all(step, '[data-kq-option]').forEach(function (node) {
      node.classList.toggle('is-on', node === option);
      node.setAttribute('aria-pressed', node === option ? 'true' : 'false');
    });

    if (key) {
      this.answers[key] = value;
      this.answers[key + '_label'] = label;
      writeStore(this.storeKey, this.answers);
    }

    this.syncCta();

    if (step.getAttribute('data-kq-auto') === 'true') {
      var self = this;
      window.setTimeout(function () {
        self.advance();
      }, 220);
    }
  };

  /* A step is complete when it has no answer key at all (intro, loading), when
     its key already holds a value, or when its required field is filled. */
  Quiz.prototype.isComplete = function (step) {
    if (!step) return false;

    var field = one(step, '[data-kq-field][data-kq-required="true"]');
    if (field) {
      var value = String(field.value || '').trim();
      if (!value) return false;
      if (field.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) return false;
      return true;
    }

    var key = step.getAttribute('data-kq-key');
    if (!key) return true;
    return !!this.answers[key];
  };

  Quiz.prototype.syncCta = function () {
    var step = this.stepAt(this.index);
    if (!step) return;
    var cta = one(step, '[data-kq-next]');
    if (!cta) return;
    cta.setAttribute('aria-disabled', this.isComplete(step) ? 'false' : 'true');
  };

  Quiz.prototype.advance = function (skipping) {
    var step = this.stepAt(this.index);
    if (!step) return;

    if (!skipping && !this.isComplete(step)) return;

    if (step.getAttribute('data-kq-capture') === 'true' && !skipping) {
      this.subscribe(step);
    }

    var next = this.nextIndex(this.index);
    if (next === -1) {
      this.finish();
      return;
    }

    this.history.push(this.index);
    this.index = next;
    this.paint();
  };

  Quiz.prototype.goBack = function () {
    if (!this.history.length) return;
    this.stopLoading();
    this.index = this.history.pop();
    this.paint();
  };

  Quiz.prototype.paint = function () {
    var self = this;
    var current = this.stepAt(this.index);

    this.steps.forEach(function (step) {
      step.classList.toggle('is-active', step === current);
    });

    /* Restore the stored answer so going back shows the earlier choice. */
    if (current) {
      var key = current.getAttribute('data-kq-key');
      if (key && this.answers[key]) {
        all(current, '[data-kq-option]').forEach(function (node) {
          var on = node.getAttribute('data-kq-option') === self.answers[key];
          node.classList.toggle('is-on', on);
          node.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      all(current, '[data-kq-field]').forEach(function (field) {
        var stored = self.answers[field.getAttribute('data-kq-field')];
        if (stored && !field.value) field.value = stored;
      });
    }

    /* Gift wording. Every node that carries alternate copy for a gift shows
       it while the stored "who" answer is gift, and its normal copy otherwise,
       so going back and changing the first answer re-words the later steps. */
    if (current) {
      var forGift = String(this.answers.who || '') === 'gift';
      all(current, '[data-kq-gift-copy]').forEach(function (node) {
        var copy = forGift ? node.getAttribute('data-kq-gift-copy') : node.getAttribute('data-kq-copy');
        if (copy === null) return;
        if (node.classList.contains('kq__sub')) node.innerHTML = copy;
        else node.textContent = copy;
      });
    }

    this.paintProgress(current);
    this.syncCta();

    if (this.back) this.back.hidden = this.history.length === 0;

    if (current && current.getAttribute('data-kq-loading') === 'true') {
      this.runLoading(current);
    }

    var focusTarget = current ? one(current, '[data-kq-field]') : null;
    if (focusTarget && window.matchMedia('(min-width: 750px)').matches) {
      window.setTimeout(function () {
        focusTarget.focus();
      }, 60);
    }

    if (current && current.getAttribute('data-kq-bare') !== 'true') {
      var top = this.root.getBoundingClientRect().top + window.pageYOffset - 90;
      if (window.pageYOffset > top + 40) window.scrollTo({ top: top, behavior: 'smooth' });
    }
  };

  Quiz.prototype.paintProgress = function (current) {
    var bar = this.root.querySelector('[data-kq-progress]');
    if (!bar) return;

    var counted = this.steps.filter(function (step) {
      return step.getAttribute('data-kq-counted') === 'true';
    });

    var bare = current && current.getAttribute('data-kq-bare') === 'true';
    bar.hidden = !!bare;

    var position = counted.indexOf(current);
    if (position === -1) return;

    if (this.track) {
      all(this.track, '[data-kq-seg]').forEach(function (seg, i) {
        seg.classList.toggle('is-on', i <= position);
      });
    }
    if (this.count) this.count.textContent = position + 1 + '/' + counted.length;
  };

  /* ------------------------------------------------------------------------
     Loading screen

     The fact swap waits for the transition to end and swaps at a true zero
     opacity, otherwise the outgoing line is still painted while the new one
     fades in and the letters read as blurred.
     ------------------------------------------------------------------------ */

  Quiz.prototype.runLoading = function (step) {
    var self = this;
    var node = one(step, '[data-kq-fact]');
    var facts = (step.getAttribute('data-kq-facts') || '')
      .split('|')
      .map(function (fact) {
        return fact.trim();
      })
      .filter(Boolean);
    var duration = parseInt(step.getAttribute('data-kq-duration'), 10) || 4500;

    this.stopLoading();

    if (node && facts.length > 1) {
      var i = 0;
      var every = Math.max(1200, Math.round(duration / facts.length));
      this.factTimer = window.setInterval(function () {
        i = (i + 1) % facts.length;
        self.swapFact(node, facts[i]);
      }, every);
    }

    this.loadingTimer = window.setTimeout(function () {
      self.stopLoading();
      self.finish();
    }, duration);
  };

  Quiz.prototype.swapFact = function (node, text) {
    var done = false;
    var apply = function () {
      if (done) return;
      done = true;
      node.textContent = text;
      void node.offsetHeight;
      node.style.opacity = '';
    };
    node.addEventListener('transitionend', apply, { once: true });
    window.setTimeout(apply, 420);
    node.style.opacity = '0';
  };

  Quiz.prototype.stopLoading = function () {
    if (this.loadingTimer) window.clearTimeout(this.loadingTimer);
    if (this.factTimer) window.clearInterval(this.factTimer);
    this.loadingTimer = null;
    this.factTimer = null;
  };

  /* ------------------------------------------------------------------------
     Email capture

     Klaviyo when the section carries a public key and a list id, otherwise a
     plain Shopify /contact capture so no address is ever lost. The profile
     write runs at the email step and again at the end; the completion event
     fires once, from finish(), after the match is known.
     ------------------------------------------------------------------------ */

  Quiz.prototype.klaviyo = function () {
    return {
      key: this.root.getAttribute('data-kq-klaviyo-key') || '',
      list: this.root.getAttribute('data-kq-klaviyo-list') || '',
      metric: this.root.getAttribute('data-kq-klaviyo-metric') || 'Kit Finder Completed',
      source: this.root.getAttribute('data-kq-klaviyo-source') || 'Kit Finder'
    };
  };

  Quiz.prototype.profile = function () {
    var out = {};
    Object.keys(this.answers).forEach(function () {});
    for (var key in this.answers) {
      if (Object.prototype.hasOwnProperty.call(this.answers, key)) out[key] = this.answers[key];
    }
    return out;
  };

  Quiz.prototype.subscribe = function () {
    var config = this.klaviyo();
    var email = this.answers.email;
    if (!email) return;

    if (!config.key || !config.list) {
      var body = new FormData();
      body.append('form_type', 'customer');
      body.append('utf8', '\u2713');
      body.append('contact[email]', email);
      body.append('contact[tags]', 'kit-finder');
      if (this.answers.first_name) body.append('contact[first_name]', this.answers.first_name);
      window.fetch('/contact', { method: 'POST', body: body }).catch(function () {});
      return;
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: config.source,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                first_name: this.answers.first_name || undefined,
                properties: this.profile()
              }
            }
          }
        },
        relationships: { list: { data: { type: 'list', id: config.list } } }
      }
    };

    window
      .fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(config.key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload)
      })
      .catch(function () {});
  };

  Quiz.prototype.trackEvent = function (extra) {
    var config = this.klaviyo();
    var email = this.answers.email;
    if (!config.key || !email) return;

    var properties = this.profile();
    for (var key in extra) {
      if (Object.prototype.hasOwnProperty.call(extra, key)) properties[key] = extra[key];
    }

    window
      .fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(config.key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: properties,
              metric: { data: { type: 'metric', attributes: { name: config.metric } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        })
      })
      .catch(function () {});
  };

  /* ------------------------------------------------------------------------
     Scoring and handover
     ------------------------------------------------------------------------ */

  Quiz.prototype.scores = function () {
    var self = this;
    var totals = {};

    all(this.root, '[data-kq-option][data-kq-weights]').forEach(function (option) {
      var step = option.closest('[data-kq-step]');
      if (!step || !self.visible(step)) return;
      var key = step.getAttribute('data-kq-key');
      if (!key || self.answers[key] !== option.getAttribute('data-kq-option')) return;

      var weights = parseWeights(option.getAttribute('data-kq-weights'));
      for (var name in weights) {
        if (Object.prototype.hasOwnProperty.call(weights, name)) {
          totals[name] = (totals[name] || 0) + weights[name];
        }
      }
    });

    return totals;
  };

  Quiz.prototype.finish = function () {
    var totals = this.scores();
    var order = Object.keys(totals).sort(function (a, b) {
      return totals[b] - totals[a];
    });

    this.answers.match = order[0] || '';
    this.answers.runner_up = order[1] || '';
    this.answers.scores = JSON.stringify(totals);
    this.answers.completed_at = new Date().toISOString();
    writeStore(this.storeKey, this.answers);

    this.subscribe();
    this.trackEvent({ match: this.answers.match, runner_up: this.answers.runner_up });

    var next = this.root.getAttribute('data-kq-result-url');
    var result = doc.querySelector('[data-kq-result]');

    this.stopLoading();

    if (result) {
      this.root.hidden = true;
      this.root.setAttribute('aria-hidden', 'true');
      showResult(result, this.answers);
      return;
    }

    if (next) window.location.href = next;
  };

  /* ------------------------------------------------------------------------
     The result screen
     ------------------------------------------------------------------------ */

  function showResult(root, answers) {
    root.classList.add('kq--done');
    root.hidden = false;

    var levelKey = root.getAttribute('data-kq-level-key') || 'level';
    var level = answers[levelKey] || '';
    var groups = all(root, '[data-kq-match]');
    if (!groups.length) return;

    var winner = null;
    groups.forEach(function (group) {
      if (group.getAttribute('data-kq-match') === answers.match) winner = group;
    });
    if (!winner) winner = groups[0];

    groups.forEach(function (group) {
      group.classList.toggle('is-on', group === winner);
    });

    /* Every level of every match is rendered server side. Reveal the one the
       shopper's experience answer points at. When that level has no kit picked
       for this world, fall back to the nearest level rather than to whatever
       card happens to be first, so an advanced builder is never handed the
       beginner kit while an intermediate one sits right there. */
    var cards = all(winner, '[data-kq-level]');
    var ladder = ['beginner', 'intermediate', 'advanced'];
    var from = ladder.indexOf(level);
    if (from === -1) from = 0;
    var preference = ladder
      .map(function (name, index) {
        return { name: name, distance: Math.abs(index - from), index: index };
      })
      .sort(function (a, b) {
        return a.distance - b.distance || a.index - b.index;
      })
      .map(function (entry) {
        return entry.name;
      });

    var chosen = null;
    preference.forEach(function (name) {
      if (chosen) return;
      cards.forEach(function (card) {
        if (!chosen && card.getAttribute('data-kq-level') === name) chosen = card;
      });
    });
    if (!chosen) chosen = cards[0];
    cards.forEach(function (card) {
      card.classList.toggle('is-on', card === chosen);
    });

    if (chosen) {
      answers.kit = chosen.getAttribute('data-kq-name') || '';
      answers.kit_url = chosen.getAttribute('data-kq-url') || '';
    }

    /* Tokens last, so [kit] resolves to the card actually on screen. */
    all(root, '[data-kq-token]').forEach(function (node) {
      var template = node.getAttribute('data-kq-token');
      node.innerHTML = fillTokens(template, answers);
    });

    /* Reward: attach the code to the cart once per result view, so the
       shopper who taps Add to cart is already discounted. Shopify answers
       /discount/CODE with a redirect that sets the discount cookie; the
       response body is not needed, and a refused code costs nothing. */
    var reward = one(root, '[data-kq-reward]');
    if (reward && reward.getAttribute('data-kq-reward-apply') === 'true' && !reward.hasAttribute('data-kq-reward-done')) {
      reward.setAttribute('data-kq-reward-done', '');
      var code = reward.getAttribute('data-kq-reward') || '';
      if (code && win.fetch) {
        try {
          win.fetch((win.Shopify && win.Shopify.routes && win.Shopify.routes.root ? win.Shopify.routes.root : '/') + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin', redirect: 'follow' }).catch(function () {});
        } catch (error) {}
      }
      var copy = one(reward, '[data-kq-copy]');
      if (copy && !copy.hasAttribute('data-kq-copy-wired')) {
        copy.setAttribute('data-kq-copy-wired', '');
        copy.addEventListener('click', function () {
          var text = copy.getAttribute('data-kq-copy') || '';
          var label = one(copy, '[data-kq-copy-label]');
          var done = function () {
            copy.classList.add('is-done');
            if (label) label.textContent = label.getAttribute('data-kq-copy-done') || label.textContent;
          };
          if (win.navigator && win.navigator.clipboard && win.navigator.clipboard.writeText) {
            win.navigator.clipboard.writeText(text).then(done, done);
          } else {
            done();
          }
        });
      }
    }

    var runner = one(root, '[data-kq-runner]');
    if (runner) {
      var source = null;
      groups.forEach(function (group) {
        if (group.getAttribute('data-kq-match') === answers.runner_up) source = group;
      });
      var card = source ? one(source, '[data-kq-level="' + (level || '') + '"]') || one(source, '[data-kq-level]') : null;
      if (card) {
        var image = card.getAttribute('data-kq-thumb');
        var thumb = one(runner, '[data-kq-runner-img]');
        if (thumb) {
          if (image) {
            thumb.src = image;
            thumb.hidden = false;
          } else {
            thumb.removeAttribute('src');
            thumb.hidden = true;
          }
        }
        var name = one(runner, '[data-kq-runner-name]');
        if (name) name.textContent = card.getAttribute('data-kq-name') || '';
        runner.href = card.getAttribute('data-kq-url') || '#';
        runner.hidden = false;
      } else {
        runner.hidden = true;
      }
    }

    var recs = all(root, '[data-kq-recs]');
    if (recs.length) {
      var picked = null;
      recs.forEach(function (group) {
        if (group.getAttribute('data-kq-recs') === answers.match) picked = group;
      });
      if (!picked) {
        recs.forEach(function (group) {
          if (group.getAttribute('data-kq-recs') === '') picked = picked || group;
        });
      }
      recs.forEach(function (group) {
        group.classList.toggle('is-on', group === picked);
      });

      /* The kit already on screen has no business appearing again two rows
         down, so it is dropped and the group is capped at three. */
      var kept = 0;
      if (picked) {
        var shownHandle = chosen ? chosen.getAttribute('data-kq-handle') : '';
        all(picked, '[data-kq-rec-handle]').forEach(function (card) {
          var duplicate = shownHandle && card.getAttribute('data-kq-rec-handle') === shownHandle;
          var over = kept >= 3;
          card.hidden = duplicate || over;
          if (!card.hidden) kept += 1;
        });
        picked.classList.toggle('is-on', kept > 0);
      }
      var recsWrap = recs[0] && recs[0].closest('.kq-res__recs');
      if (recsWrap) recsWrap.hidden = kept < 1;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    window.setTimeout(function () {
      var top = root.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 30);
  }

  /* Add to cart from the result. One line, then the drawer, using the same
     handover the rest of the Kopstorm work on this store uses. */
  function bindResultCart(root) {
    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-kq-add]');
      if (!button) return;
      event.preventDefault();

      var id = button.getAttribute('data-kq-add');
      if (!id || button.getAttribute('aria-disabled') === 'true') return;

      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');

      window
        .fetch(routes().cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1, properties: { _kop_source: 'kit-finder' } }] })
        })
        .then(function (response) {
          return response.json();
        })
        .then(function (state) {
          if (state && state.status) throw new Error(state.description || state.message || 'cart error');
          return window
            .fetch(routes().cart_url || '/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS) {
                theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kopstorm-quiz', cart: cart });
              }
              doc.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
              openCart();
            });
        })
        .catch(function (error) {
          console.error('Kit finder add failed', error);
          var url = button.getAttribute('data-kq-url');
          if (url) window.location.href = url;
        })
        .finally(function () {
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
        });
    });

    var restart = one(root, '[data-kq-restart]');
    if (restart) {
      restart.addEventListener('click', function () {
        var quiz = doc.querySelector('[data-kq-root]');
        clearStore(quiz ? quiz.getAttribute('data-kq-storage') : 'kopstorm_kit_quiz');
        window.location.href = restart.getAttribute('data-kq-restart') || window.location.pathname;
      });
    }
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function init() {
    all(doc, '[data-kq-root]').forEach(function (root) {
      if (root.kqReady) return;
      root.kqReady = true;
      new Quiz(root);
    });

    all(doc, '[data-kq-result]').forEach(function (root) {
      if (root.kqReady) return;
      root.kqReady = true;
      bindResultCart(root);

      /* A result on its own page, or a reload after the flow finished. */
      if (root.getAttribute('data-kq-start-hidden') !== 'true') {
        var quiz = doc.querySelector('[data-kq-root]');
        var answers = readStore(quiz ? quiz.getAttribute('data-kq-storage') : root.getAttribute('data-kq-storage'));
        if (answers && answers.match) showResult(root, answers);
        else showResult(root, {});
      }
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  doc.addEventListener('shopify:section:load', init);

  window.KopstormKitFinder = {
    reset: function () {
      var quiz = doc.querySelector('[data-kq-root]');
      clearStore(quiz ? quiz.getAttribute('data-kq-storage') : 'kopstorm_kit_quiz');
      window.location.reload();
    },
    answers: function () {
      var quiz = doc.querySelector('[data-kq-root]');
      return readStore(quiz ? quiz.getAttribute('data-kq-storage') : 'kopstorm_kit_quiz');
    }
  };
})();
